"use client";

import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SidePanel } from "@speakai/ui/side-panel";
import { Button } from "@speakai/ui/button";
import { Input, Textarea } from "@speakai/ui/input";
import { Combobox } from "@/components/shared/combobox";
import { Switch } from "@speakai/ui/switch";
import { useToast } from "@speakai/ui/toast";
import { LoaderCircle, X } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { invalidateFieldDefinitions } from "@/queries/field-invalidation";
import { track } from "@/lib/integrations/sentry-tracking";

export interface FieldEditPanelField {
  id: string;
  name: string;
  description: string;
  type: string;
  privacyMode: string;
  options?: string[];
  prompt?: string;
  notApplicableValues?: string;
  allowedValues?: string[];
  allowedValuesMode?: "single" | "multiple";
  otherValues?: boolean;
}

interface FieldEditPanelProps {
  open: boolean;
  onClose: () => void;
  /** When provided, panel is in edit mode for this field. When null, in create mode. */
  field: FieldEditPanelField | null;
  /** Called after successful save with the saved field (for parent auto-select etc.). */
  onSaved?: (field: FieldEditPanelField) => void;
  /** Lift the panel above modal dialogs when opened from inside one. */
  elevated?: boolean;
}

interface FormState {
  name: string;
  type: string;
  description: string;
  prompt: string;
  notApplicableValues: string;
  allowedValues: string[];
  allowedValuesMode: "single" | "multiple";
  otherValues: boolean;
  privacyMode: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  type: "text",
  description: "",
  prompt: "",
  notApplicableValues: "",
  allowedValues: [],
  allowedValuesMode: "multiple",
  otherValues: false,
  privacyMode: "public",
};

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & Time" },
  { value: "boolean", label: "Boolean" },
  { value: "url", label: "URL" },
  { value: "currency", label: "Currency" },
];

const MAX_ALLOWED_VALUES = 50;

/**
 * Shared field create/edit panel. Used by:
 * - Profile → Custom Fields page (top-level field management)
 * - Automation dialog field picker (inline edit per field row)
 *
 * Opens as a SidePanel so it can stack over other panels (e.g. nested over
 * the automation dialog). On save, invalidates the field definitions so any
 * consumer of useFields gets fresh data.
 */
export function FieldEditPanel({ open, onClose, field, onSaved, elevated }: FieldEditPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEdit = field !== null;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [allowedValuesInput, setAllowedValuesInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (field) {
      setForm({
        name: field.name,
        type: field.type,
        description: field.description,
        prompt: field.prompt ?? "",
        notApplicableValues: field.notApplicableValues ?? "",
        allowedValues: field.allowedValues ?? [],
        allowedValuesMode: field.allowedValuesMode ?? "multiple",
        otherValues: field.otherValues ?? false,
        privacyMode: field.privacyMode ?? "public",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setAllowedValuesInput("");
  }, [open, field]);

  function addAllowedValue(raw: string) {
    const trimmed = raw.trim().replace(/,$/, "");
    if (!trimmed) return;
    setForm((prev) => {
      if (prev.allowedValues.includes(trimmed)) return prev;
      if (prev.allowedValues.length >= MAX_ALLOWED_VALUES) {
        toast.error(t("PROFILE.FIELDS_FORM_ALLOWED_VALUES_MAX"));
        return prev;
      }
      return { ...prev, allowedValues: [...prev.allowedValues, trimmed] };
    });
    setAllowedValuesInput("");
  }

  function removeAllowedValue(index: number) {
    setForm((prev) => ({
      ...prev,
      allowedValues: prev.allowedValues.filter((_, i) => i !== index),
    }));
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error(t("PROFILE.FIELDS_NAME_REQUIRED"));
      return;
    }

    setIsSaving(true);

    const payload = {
      name: form.name,
      type: form.type,
      description: form.description,
      prompt: form.prompt.trim() || undefined,
      notApplicableValues: form.notApplicableValues.trim() || undefined,
      allowedValues: form.allowedValues.length > 0 ? form.allowedValues : undefined,
      allowedValuesMode: form.allowedValues.length > 0 ? form.allowedValuesMode : undefined,
      otherValues: form.allowedValues.length > 0 ? form.otherValues : undefined,
      privacyMode: form.privacyMode,
    };

    try {
      let saved: FieldEditPanelField | null = null;
      if (isEdit && field) {
        const res = await apiClient.put<{ status: string }>(`/fields/${field.id}`, payload);
        if (res.success) {
          toast.success(t("PROFILE.FIELDS_UPDATED"));
          track("fields", "edit");
          saved = { ...field, ...payload, allowedValues: payload.allowedValues ?? [] };
        } else {
          toast.error(res.error ?? t("PROFILE.FIELDS_UPDATE_FAILED"));
          return;
        }
      } else {
        const res = await apiClient.post<FieldEditPanelField>("/fields", payload);
        if (res.success && res.data) {
          toast.success(t("PROFILE.FIELDS_CREATED"));
          track("fields", "create");
          saved = res.data;
        } else {
          toast.error(res.error ?? t("PROFILE.FIELDS_CREATE_FAILED"));
          return;
        }
      }

      await invalidateFieldDefinitions(queryClient);
      if (saved && onSaved) onSaved(saved);
      onClose();
    } catch {
      toast.error(t("ERROR_NOTIFICATION.UNKNOWN_ERROR"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? t("PROFILE.FIELDS_DIALOG_EDIT_TITLE")
          : t("PROFILE.FIELDS_DIALOG_CREATE_TITLE")
      }
      size="default"
      resizable
      panelId="field-edit"
      elevated={elevated}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("ACTION.CANCEL")}
          </Button>
          <Button onClick={save} disabled={isSaving}>
            {isSaving ? (
              <>
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                {t("ACTION.SAVING")}
              </>
            ) : isEdit ? (
              t("PROFILE.FIELDS_FORM_UPDATE")
            ) : (
              t("PROFILE.FIELDS_FORM_CREATE")
            )}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4 space-y-5">
        <div>
          <label htmlFor="fep-name" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_NAME")} <span aria-hidden="true">*</span>
          </label>
          <Input
            id="fep-name"
            aria-required="true"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder={t("PROFILE.FIELDS_FORM_NAME_PLACEHOLDER")}
          />
        </div>

        <div>
          <label htmlFor="fep-type" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_TYPE")}
          </label>
          <Combobox
            id="fep-type"
            value={form.type}
            onChange={(val) => setForm((p) => ({ ...p, type: val }))}
            options={FIELD_TYPE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(`PROFILE.FIELDS_TYPE_${opt.value.toUpperCase()}`, opt.label),
            }))}
            hideSearch
          />
          {isEdit && (
            <p className="text-xs text-muted-foreground mt-1">
              {t("PROFILE.FIELDS_TYPE_CONVERSION_NOTE")}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="fep-desc" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_DESCRIPTION")}
          </label>
          <Input
            id="fep-desc"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder={t("PROFILE.FIELDS_FORM_DESCRIPTION_PLACEHOLDER")}
          />
        </div>

        <div>
          <label htmlFor="fep-privacy" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_PRIVACY")}
          </label>
          <Combobox
            id="fep-privacy"
            value={form.privacyMode}
            onChange={(val) => setForm((p) => ({ ...p, privacyMode: val }))}
            options={[
              { value: "public", label: t("PROFILE.FIELDS_FORM_PRIVACY_PUBLIC") },
              { value: "private", label: t("PROFILE.FIELDS_FORM_PRIVACY_PRIVATE") },
            ]}
            hideSearch
          />
          <p className="text-xs text-muted-foreground mt-1">
            {t("PROFILE.FIELDS_FORM_PRIVACY_HINT")}
          </p>
        </div>

        <div>
          <label htmlFor="fep-prompt" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_PROMPT")}
          </label>
          <Textarea
            id="fep-prompt"
            value={form.prompt}
            onChange={(e) => setForm((p) => ({ ...p, prompt: e.target.value }))}
            placeholder={t("PROFILE.FIELDS_FORM_PROMPT_PLACEHOLDER")}
            rows={3}
            maxLength={500}
            data-sentry-mask
          />
          <p className="text-xs text-muted-foreground mt-1">
            {t("PROFILE.FIELDS_FORM_PROMPT_HINT")}
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_ALLOWED_VALUES")}
          </label>
          {form.allowedValues.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-sentry-mask>
              {form.allowedValues.map((val, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium"
                >
                  {val}
                  <button
                    type="button"
                    onClick={() => removeAllowedValue(i)}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={t("PROFILE.FIELDS_FORM_ALLOWED_REMOVE", { value: val })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <Input
            value={allowedValuesInput}
            onChange={(e) => setAllowedValuesInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addAllowedValue(allowedValuesInput);
              }
            }}
            onBlur={() => {
              if (allowedValuesInput.trim()) addAllowedValue(allowedValuesInput);
            }}
            placeholder={t("PROFILE.FIELDS_FORM_ALLOWED_VALUES_PLACEHOLDER")}
            aria-label={t("PROFILE.FIELDS_FORM_ALLOWED_VALUES")}
            data-sentry-mask
          />
          <p className="text-xs text-muted-foreground mt-1">
            {t("PROFILE.FIELDS_FORM_ALLOWED_VALUES_HINT")}
          </p>
        </div>

        {form.allowedValues.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {form.allowedValuesMode === "multiple"
                    ? t("PROFILE.FIELDS_FORM_MODE_MULTIPLE")
                    : t("PROFILE.FIELDS_FORM_MODE_SINGLE")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("PROFILE.FIELDS_FORM_MODE_HINT")}
                </p>
              </div>
              <Switch
                checked={form.allowedValuesMode === "multiple"}
                onChange={(checked) =>
                  setForm((prev) => ({
                    ...prev,
                    allowedValuesMode: checked ? "multiple" : "single",
                  }))
                }
                aria-label={t("PROFILE.FIELDS_FORM_MODE_TOGGLE")}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("PROFILE.FIELDS_FORM_OTHER_VALUES")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("PROFILE.FIELDS_FORM_OTHER_VALUES_HINT")}
                </p>
              </div>
              <Switch
                checked={form.otherValues}
                onChange={(checked) =>
                  setForm((prev) => ({ ...prev, otherValues: checked }))
                }
                aria-label={t("PROFILE.FIELDS_FORM_OTHER_VALUES")}
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="fep-na" className="text-sm font-medium text-foreground mb-1 block">
            {t("PROFILE.FIELDS_FORM_NA_VALUES")}
          </label>
          <Input
            id="fep-na"
            value={form.notApplicableValues}
            onChange={(e) => setForm((p) => ({ ...p, notApplicableValues: e.target.value }))}
            placeholder={t("PROFILE.FIELDS_FORM_NA_VALUES_PLACEHOLDER")}
            data-sentry-mask
          />
          <p className="text-xs text-muted-foreground mt-1">
            {t("PROFILE.FIELDS_FORM_NA_VALUES_HINT")}
          </p>
        </div>
      </div>
    </SidePanel>
  );
}
